import * as React from 'react'
import { cn } from '../../lib/utils'

export interface ListProps {
  children: React.ReactNode
  className?: string
  role?: string
}

export interface ListItemProps {
  title?: React.ReactNode
  subtitle?: React.ReactNode
  status?: React.ReactNode
  right?: React.ReactNode
  className?: string
  href?: string
  onClick?: React.MouseEventHandler<HTMLAnchorElement | HTMLDivElement>
  children?: React.ReactNode
}

const baseListClasses =
  'divide-y divide-gray-100 overflow-hidden bg-white shadow-sm outline outline-1 outline-gray-900/5 sm:rounded-xl dark:divide-white/5 dark:bg-gray-800/50 dark:shadow-none dark:outline-white/10 dark:sm:-outline-offset-1'

const baseItemClasses =
  'relative flex justify-between gap-x-6 px-4 py-5 hover:bg-gray-50 sm:px-6 dark:hover:bg-white/[0.025]'

export function List({ children, className, role = 'list' }: ListProps) {
  return (
    <ul role={role} className={cn(baseListClasses, className)}>
      {children}
    </ul>
  )
}

function ListItemContent({ title, subtitle, status }: Pick<ListItemProps, 'title' | 'subtitle' | 'status'>) {
  return (
    <div className="min-w-0 flex-auto">
      {(title || status) && (
        <div className="flex items-start gap-x-3">
          {title && <p className="text-sm/6 font-semibold text-foreground">{title}</p>}
          {status}
        </div>
      )}
      {subtitle && (
        <div className="mt-1 flex text-xs/5 text-gray-500 dark:text-gray-400">{subtitle}</div>
      )}
    </div>
  )
}

function ListItemBase({
  title,
  subtitle,
  status,
  right,
  className,
  href,
  onClick,
  children,
}: ListItemProps) {
  const content = (
    <li className={cn(baseItemClasses, className)}>
      {children ? children : <ListItemContent title={title} subtitle={subtitle} status={status} />}
      {right && <div className="flex shrink-0 items-center gap-x-4">{right}</div>}
    </li>
  )

  if (href) {
    return (
      <a href={href} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-gray-900" onClick={onClick as any}>
        {content}
      </a>
    )
  }

  if (onClick) {
    return (
      <div role="button" onClick={onClick as any} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
        {content}
      </div>
    )
  }

  return content
}

List.Item = ListItemBase as unknown as React.FC<ListItemProps>

export namespace ListNS {
  export type ItemProps = ListItemProps
}

export type { ListItemProps as ListItemPropsType }

export default List
